class NotificationSoundService {
  constructor() {
    this._enabled = localStorage.getItem('notif_sound_enabled') !== 'false';
    this._volume = parseFloat(localStorage.getItem('notif_sound_volume') || '0.7');
    this._audioCtx = null;
    this._messageBuffer = null;
    this._callSource = null;
    this._callFallback = null;
    this._unlocked = false;
    this._preloaded = false;
    this._setupUnlock();
  }

  _getCtx() {
    if (!this._audioCtx) {
      try {
        this._audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      } catch (e) { return null; }
    }
    return this._audioCtx;
  }

  // iOS Safari requires audio unlock after user interaction
  _setupUnlock() {
    const unlock = async () => {
      if (this._unlocked) return;
      try {
        const ctx = this._getCtx();
        if (!ctx) return;
        if (ctx.state === 'suspended') await ctx.resume();
        const buf = ctx.createBuffer(1, 1, 22050);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(ctx.destination);
        src.start(0);
        this._unlocked = true;
        if (!this._preloaded) this._preloadSounds();
      } catch (e) {}
      document.removeEventListener('click', unlock);
      document.removeEventListener('touchstart', unlock);
      document.removeEventListener('keydown', unlock);
    };
    document.addEventListener('click', unlock, { passive: true });
    document.addEventListener('touchstart', unlock, { passive: true });
    document.addEventListener('keydown', unlock, { passive: true });
  }

  async _loadBuffer(url) {
    const ctx = this._getCtx();
    if (!ctx) throw new Error('No AudioContext');
    const res = await fetch(url);
    const ab = await res.arrayBuffer();
    return new Promise((resolve, reject) => {
      ctx.decodeAudioData(ab, resolve, reject);
    });
  }

  // Pre-load audio into AudioBuffer to avoid reload on every notification
  async _preloadSounds() {
    this._preloaded = true;
    try {
      this._messageBuffer = await this._loadBuffer('/message.mp3');
    } catch {
      try {
        this._messageBuffer = await this._loadBuffer('/message.wav');
      } catch (e) {
        console.warn('[Sound] Preload failed:', e);
      }
    }
  }

  _playBuffer(buffer, loop = false) {
    const ctx = this._getCtx();
    if (!ctx || !buffer) return null;
    try {
      if (ctx.state === 'suspended') ctx.resume();
      const gain = ctx.createGain();
      gain.gain.value = this._volume;
      gain.connect(ctx.destination);
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = loop;
      src.connect(gain);
      src.start(0);
      return src;
    } catch (e) {
      console.warn('[Sound] Play failed:', e);
      return null;
    }
  }

  _fallback(url, loop = false) {
    try {
      const a = new Audio(url);
      a.volume = this._volume;
      a.loop = loop;
      a.play().catch(() => {});
      return a;
    } catch { return null; }
  }

  playMessage() {
    if (!this._enabled) return;
    if (this._messageBuffer) {
      this._playBuffer(this._messageBuffer);
    } else {
      this._fallback('/message.mp3');
    }
  }

  // Different tone for payment (ascending two-note chime via WebAudio oscillator)
  playPayment() {
    if (!this._enabled) return;
    try {
      const ctx = this._getCtx();
      if (!ctx) { this._fallback('/message.wav'); return; }
      if (ctx.state === 'suspended') ctx.resume();
      [880, 1320].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const t = ctx.currentTime + i * 0.18;
        gain.gain.setValueAtTime(this._volume * 0.45, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.35);
      });
    } catch {
      this._fallback('/message.wav');
    }
  }

  playCall() {
    if (!this._enabled) return;
    this.stopCall();
    if (this._messageBuffer) {
      this._callSource = this._playBuffer(this._messageBuffer, true);
    } else {
      this._callFallback = this._fallback('/message.wav', true);
    }
  }

  stopCall() {
    if (this._callSource) {
      try { this._callSource.stop(); } catch {}
      this._callSource = null;
    }
    if (this._callFallback) {
      try { this._callFallback.pause(); this._callFallback.currentTime = 0; } catch {}
      this._callFallback = null;
    }
  }

  setVolume(v) {
    this._volume = Math.max(0, Math.min(1, v));
    localStorage.setItem('notif_sound_volume', String(this._volume));
  }

  getVolume() { return this._volume; }

  setEnabled(b) {
    this._enabled = !!b;
    localStorage.setItem('notif_sound_enabled', String(this._enabled));
    if (!b) this.stopCall();
  }

  isEnabled() { return this._enabled; }

  testSound() {
    const prev = this._enabled;
    this._enabled = true;
    this.playMessage();
    this._enabled = prev;
  }
}

export const notificationSound = new NotificationSoundService();
export default notificationSound;
